<!-- 
  MmfEdit 组件
  v0.1.0
  传入一个实体名称，根据该实体中的 gridFields 生成编辑菜单。
  作为对组件库的补充。
  参考 views/prd/mixingMgt/MixingPlanEdit

  如何使用该组件？
  该组件基于 model 目录下的 gridField 自动配置生成输入框
  兼容为 MmfTable 设计的 gridField, 也就是说你可以不修改 model 直接使用。
  但如果需要功能扩展，可以在标准 MmfTable 的 gridField 基础上，添加了一些属性，
  具体可以参考 mixingPlan/prd/mixingMgt/mixingPlan

  TODO: - 具体输入框的生成应该使用通用组件，类似 FormHelper
        - 数据合法性校验应该在数据输入时，而非在保存时
 -->
<template>
  <div class="row">
    <!-- 依据配置生成修改框 -->
    <div class="form-group col-md-3" v-for="(item, index) in editableGridFields" :key="index">
      <label class="control-label"><span class="lbl-obg" v-if="item.required">*</span>{{$t(item.label)}}:</label>
      <!-- <input
        class="form-control form-control-sm"
        :type="item.form.type==='input'?'text':(item.form.type||'text')"
        v-if="['input', 'time', 'date'].includes(item.form.type)"
        v-model="valuesWithCallback[item.key]"
      />
      <select
        class="form-control form-control-sm"
        v-else-if="item.form.type==='select'"
        v-model="valuesWithCallback[item.key]"
      >
        <option
          :value="op.value"
          v-for="(op, index) in item.form.options"
          :key="index"
        >{{op.text||op.value}}</option>
      </select>-->
      <mmf-form-helper
        is-editing
        :name="item.key"
        :metadata="item"
        :value="valuesWithCallback[item.key]"
        @input="v=>valuesWithCallback[item.key]=v"
        @bind="v=>valuesWithCallback[item.key]=v"
      />
    </div>
  </div>
</template>

<script>
import MmfFormHelper from "@/components/MmfFormHelper";
import { createHelpers } from "vuex-map-fields";
import model from "@/model/index";
import gridFieldsReader from "@/utils/gridFieldsReader";
import { date2str } from "@/utils/day";

export default {
  components: { MmfFormHelper },
  props: {
    // 当前实体的名称，如 'mixingJob', 'systemParameter'
    // 该名称将被用来访问 store, model
    entity: String
  },
  computed: {
    /**
     * 表单中的所有对象
     * @returns {Array<GridField>}
     */
    allGridFields() {
      return model[this.entity].gridFields(); // 编辑菜单
    },
    /**
     * 表单中的可编辑对象
     * @returns {Array<GridField>}
     */
    editableGridFields() {
      return this.allGridFields.filter(v => !v.uneditable); //（滤除不可编辑对象）
    },
    /**
     * vuex entity 属性访问器的数组：
     * [...mapField]
     * 里面的键值包括 gridFields 中的所有 key
     * @returns {Array<{[key:string]:any}>}
     */
    values() {
      // 通常 mapFields 在外部初始化，这里因为需要读取 entity 模块的名称
      // 因而将初始化放在了函数中
      const { mapFields } = createHelpers({
        getterType: `${this.entity}/getField`,
        mutationType: `${this.entity}/updateField`
      });
      const gridFields = this.allGridFields;
      const fieldNames = gridFields.map(v => `entity.${v.key}`);
      // 通常将 mapFields 的结果展开
      // 但是这里我们希望将这些对象作为一个整体使用，所以要手动生成访问器
      let getSetMap = mapFields(fieldNames);

      const result = {};
      gridFields.forEach(v => {
        Object.defineProperty(result, v.key, {
          enumerable: true,
          configurable: true,
          get: () => {
            return getSetMap[v.key].get.call(this);
          },
          set: value => {
            // getSetMap[v.key].set.call(this, value);
            // 根据 entity 中的设计，entity 中的属性是不受维护的。
            // 可以随意被覆盖或者设置为空值，所以不能跟踪绑定到 具体属性
            // 而是应该绑定到 entity 一级
            // 后面的语句的作用是强制触发更新
            let entity = { ...this.$store.state[this.entity].entity };
            entity[v.key] = value;
            this.$store.commit(this.entity + "/setEntity", entity);
          }
        });
      });
      return result;
    },
    /**
     * 带有数据预处理的访问器
     * 根据编辑菜单构建绑定到菜单上的访问器
     * return {[name:string]:{
     *  getter: () => T;
     *  setter: (value: T) => void;
     * }}
     * @returns {Array<{[key:string]:any}>}
     */
    valuesWithCallback() {
      const result = {};
      const values = this.values;
      const allGridFields = this.allGridFields;
      allGridFields.forEach(v => {
        const key = v.key;
        const callbackGetList = [gridFieldsReader.callbackGet(v)];
        const callbackSetList = [gridFieldsReader.callbackSet(v)];

        // 当类型是时间的时候，加上规范化时间的预处理
        // 比如原本是 2019-01-01 00:00 不能直接被 input 使用
        // 通过这个预处理后 变成 2019-01-01
        // if (v.form.type === "date") {
        //   callbackGetList.push(v => date2str(new Date(v)));
        // }

        Object.defineProperty(result, key, {
          enumerable: true,
          configurable: true,
          get: () => {
            return callbackGetList.reduce(
              (data, func) => func(data),
              values[key]
            );
          },
          set: newValue => {
            values[key] = callbackSetList.reduce(
              (data, func) => func(data),
              newValue
            );
          }
        });
      });
      return result;
    }
  },
  methods: {
    /**
     * 相比 base 中的 MMFSave 方法，
     * 这里的 save 方法加入了表单保存校验
     * @returns {boolean} 是否通过了校验检查
     * 如果 base/MMFSave 返回了 promise
     * 这里返回 promise 会比 boolean 更好
     */
    save() {
      const entity = this.entity;
      // 不可编辑的对象也有可能需要被处理，因而使用 allGridFields 而非 editableGridFields
      // 例如需求：保存时自动设置 '计划修改时间' 为当前时间
      // 而计划保存时间是 uneditable: true 的，使用 editableGridFields 就会导致无法触发。
      const gridFields = this.allGridFields; // 需要被处理的数据
      const values = this.values; // 数据的触发器

      // 校验数据
      if (!this.checkValues(values)) {
        return false;
      }

      // 保存前预处理数据
      for (let v of gridFields) {
        const key = v.key;
        const save = gridFieldsReader.callbackSave(v);
        values[key] = save(values[key]);
      }

      // 发送保存事件
      this.$store.dispatch("base/MMFSave", entity);
      return true;
    },
    /**
     * 参数校验
     * @returns {boolean} 是否通过了参数校验
     */
    checkValues(values) {
      const gridFields = this.allGridFields;
      return gridFields.every(field => {
        const check = gridFieldsReader.callbackCheck(field);
        const key = field.key;
        const v = values[key];
        const isRight = check(v);
        if (!isRight) {
          const userAlertWarningStr = `${key} should not be ${
            v ? `'${v}'` : "empty"
          }`;

          this.$store.dispatch(
            "base/showWarning",
            { userAlertWarningStr, userAlertWarningVisible: "false" },
            { root: true }
          );
        }
        return isRight;
      });
    }
  }
};
</script>


<!--
  v0.0.1
    之前版本中是直接对 entity 的属性进行更新，如果 entity 之前没有这个属性。
    更新后，可能导致其他使用该属性的节点无法获知更新。
    该版本将每次属性修改都变为了赋值 entity，从而强行触发更新。
  -->
